import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { State } from '../utils/context'
import { highlight, parseQuery, formatReference } from '../utils/query'
import { useSearch, resultKey, inFilter, PAGE_SIZE } from '../utils/search'
import { useEventListener } from '../utils/hooks'
import { BIBLE_CODES, bookNames } from '../utils/bibles'
import Verse from '../components/verse'
import Chip from '../components/chip'
import ArrowRight from '../icons/arrow-right'
import styles from './results.module.css'

const BOOKS_COLLAPSED = 8
const EXAMPLES = ['J 3,16', '1 Kor 13,4-7', 'Ps 23', 'Mt: królestwo', '"na początku"']

const plural = (count, one, few, many) => (
  count == 1
    ? one
    : count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14) ? few : many
)

const Results = () => {
  const [{input, search, active}, setState] = useContext(State)
  const {navigate, find, submit} = useSearch()
  const [expanded, setExpanded] = useState(false)
  const sentinel = useRef()
  const scroll = useRef(search.scroll)

  const {suggestions} = useMemo(() => parseQuery(search.input, input.book), [search.input, input.book])
  const filtered = useMemo(() => search.results.filter(match => inFilter(search.filter, match.book)), [search.results, search.filter])

  const counts = useMemo(() => {
    const counts = {all: search.results.length, ot: 0, nt: 0, books: {}}

    for (const {book} of search.results) {
      counts[book <= 39 ? 'ot' : 'nt']++
      counts.books[book] = (counts.books[book] ?? 0) + 1
    }

    return counts
  }, [search.results])

  const groups = useMemo(() => filtered.slice(0, search.shown).reduce((groups, match) => {
    const last = groups.at(-1)
    last?.book == match.book ? last.matches.push(match) : groups.push({book: match.book, matches: [match]})
    return groups
  }, []), [filtered, search.shown])

  const books = Object.keys(counts.books)
  const names = bookNames(search.bible ?? input.bible)
  const visibleBooks = expanded ? books : books.slice(0, BOOKS_COLLAPSED)

  const setSearch = value => setState(state => ({
    ...state,
    search: {
      ...state.search,
      ...value
    }
  }))

  const onToggle = key => setState(state => ({
    ...state,
    active: {
      ...state.active,
      verses: {
        ...state.active.verses,
        [key]: !state.active.verses[key]
      }
    }
  }))

  const onFilter = filter => {
    setSearch({filter, shown: PAGE_SIZE})
    scrollTo({top: 0})
  }

  const onQuery = query => {
    setSearch({input: query})
    submit(query)
  }

  const onBible = bible => {
    setState(state => ({
      ...state,
      input: {
        ...state.input,
        bible
      }
    }))
    find(search.query, {bible})
  }

  // Po odświeżeniu strony lub zmianie przekładu wyniki trzeba wyszukać ponownie.
  useEffect(() => {
    search.input && search.query && (search.status == 'idle' || search.bible != input.bible) && find(search.query)
  }, [])

  // Powrót do wyników przywraca miejsce, w którym je opuszczono. Pozycję zapisujemy na bieżąco,
  // bo po zniknięciu listy przeglądarka zdąży już przyciąć przewinięcie do niższej strony.
  useEventListener(window, 'scroll', () => scroll.current = scrollY, [])

  useEffect(() => {
    scrollTo({top: search.scroll})
    setSearch({back: false})

    return () => setSearch({scroll: scroll.current})
  }, [])

  // Kolejne wyniki dokładamy, gdy lista zbliża się do końca ekranu.
  useEffect(() => {
    if (!sentinel.current) return

    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && setState(state => ({
      ...state,
      search: {
        ...state.search,
        shown: state.search.shown + PAGE_SIZE
      }
    })), {rootMargin: '600px'})

    observer.observe(sentinel.current)

    return () => observer.disconnect()
  }, [filtered, search.shown])

  const summary = search.status == 'loading'
    ? search.progress < 1 && !search.results.length
      ? `Wczytywanie przekładu… ${Math.round(search.progress * 100)}%`
      : 'Szukam…'
    : search.status == 'done'
      ? search.results.length
        ? `${search.results.length} ${plural(search.results.length, 'wynik', 'wyniki', 'wyników')} w ${books.length} ${plural(books.length, 'księdze', 'księgach', 'księgach')}`
        : `Brak wyników dla „${search.query.trim()}” w przekładzie ${BIBLE_CODES[search.bible]}.`
      : search.status == 'error'
        ? 'Nie udało się przeszukać tekstu. Sprawdź połączenie z internetem.'
        : ''

  return (
    <div className={styles.results}>
      {suggestions.length > 0 && (
        <div className={styles.chips} aria-label="Go to">
          {suggestions.map(reference => (
            <Chip
              key={`${reference.book}.${reference.chapter}`}
              onClick={() => navigate(reference, search.input, {clear: true})}
              data-search-item
            >
              {formatReference(reference, input.bible)} →
            </Chip>
          ))}
        </div>
      )}

      {!search.input.trim() && (
        <>
          {search.history.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.label}>Ostatnie wyszukiwania</h2>
              <div className={styles.chips}>
                {search.history.map(query => <Chip key={query} onClick={() => onQuery(query)} data-search-item>{query}</Chip>)}
              </div>
            </section>
          )}
          <section className={styles.section}>
            <h2 className={styles.label}>Wpisz słowo, frazę w cudzysłowie lub odnośnik, np.</h2>
            <div className={styles.chips}>
              {EXAMPLES.map(query => <Chip key={query} onClick={() => onQuery(query)} data-search-item>{query}</Chip>)}
            </div>
          </section>
        </>
      )}

      {search.input.trim() && search.query && (
        <>
          <div className={styles.summary} aria-live="polite">{summary}</div>

          {search.status == 'done' && !search.results.length && (
            <div className={styles.chips}>
              {Object.entries(BIBLE_CODES).filter(([id]) => id != search.bible).map(([id, name]) => (
                <Chip key={id} onClick={() => onBible(+id)}>Szukaj w {name}</Chip>
              ))}
            </div>
          )}

          {search.results.length > 0 && books.length > 1 && (
            <div className={styles.chips} aria-label="Filter">
              <Chip active={!search.filter} count={counts.all} onClick={() => onFilter(null)}>Wszystkie</Chip>
              {counts.ot > 0 && counts.nt > 0 && (
                <>
                  <Chip active={search.filter == 'ot'} count={counts.ot} onClick={() => onFilter('ot')}>Stary Testament</Chip>
                  <Chip active={search.filter == 'nt'} count={counts.nt} onClick={() => onFilter('nt')}>Nowy Testament</Chip>
                </>
              )}
              {visibleBooks.map(book => (
                <Chip key={book} active={search.filter == book} count={counts.books[book]} onClick={() => onFilter(+book)}>
                  {names[book]}
                </Chip>
              ))}
              {books.length > BOOKS_COLLAPSED && (
                <Chip onClick={() => setExpanded(!expanded)}>
                  {expanded ? 'Mniej' : `+${books.length - BOOKS_COLLAPSED} ${plural(books.length - BOOKS_COLLAPSED, 'księga', 'księgi', 'ksiąg')}`}
                </Chip>
              )}
            </div>
          )}

          {groups.map(({book, matches}) => (
            <section key={`${book}.${matches[0].chapter}.${matches[0].verse}`} className={styles.group}>
              <h2 className={styles.heading}>
                {names[book]}
                <span className={styles.count}>{counts.books[book]}</span>
              </h2>
              {matches.map(match => {
                const key = resultKey(match)
                const reference = {book, chapter: match.chapter, verses: [[match.verse, match.verse]]}

                return (
                  <div key={key} className={styles.result}>
                    <Verse
                      className={styles.match}
                      active={active.verses[key]}
                      onClick={() => onToggle(key)}
                      id={`${match.chapter}:${match.verse}`}
                      data-search-item
                    >
                      {highlight(match.text, search.terms).map(([text, mark], index) => mark
                        ? <mark key={index}>{text}</mark>
                        : text
                      )}
                    </Verse>
                    <button
                      className={styles.open}
                      onClick={() => navigate(reference, search.query, {back: true})}
                      aria-label={`Open ${formatReference(reference, search.bible)}`}
                    >
                      <ArrowRight />
                    </button>
                  </div>
                )
              })}
            </section>
          ))}

          {search.shown < filtered.length && <div ref={sentinel} className={styles.sentinel} />}
        </>
      )}
    </div>
  )
}

export default Results
